import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAbsentLast5 } from "../../actions/AbsentLast3Action";
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  useTheme, 
  useMediaQuery,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Backdrop,
  LinearProgress,
  IconButton,
  Tooltip,
  Menu,
  TextField,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Badge,
  Popover,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LanguageIcon from "@mui/icons-material/Language";
import TranslateIcon from "@mui/icons-material/Translate";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ------------ Translation Texts ------------
const translations = {
  en: {
    title: "Absenteeism Report (3+ Days)",
    fromDate: "From",
    toDate: "To",
    reportPeriod: "Period",
    datePicker: "Select Date",
    exportPdf: "Export PDF",
    exporting: "Exporting...",
    totalAbsent: "Total Absent",
    location: "Location",
    serviceNo: "Service No",
    name: "Employee Name",
    absentDays: "Absent Days",
    lastAttendance: "Last Attendance",
    loading: "Loading absent employees data...",
    noData: "No absent employees data available",
    noMatchingRecords: "No matching records found with current filters",
    reportTitle: "Absenteeism Report",
    reportDate: "Report Date",
    page: "Page",
    of: "of",
    generatedOn: "Generated on",
    pdfSuccess: "PDF exported successfully!",
    pdfError: "Failed to export PDF. Please try again.",
    language: "Language",
    searchPlaceholder: "Search by Service No, Name or Location...",
    filterByLocation: "Filter by Location",
    selectAll: "Select All",
    clear: "Clear",
    apply: "Apply",
    clearAll: "Clear All",
    activeFilters: "Active Location Filters:",
    locationsAffected: "Locations Affected",
    showing: "Showing",
    of: "of",
    records: "records",
    filteredBy: "filtered by",
    location_one: "location",
    location_other: "locations",
    allLocations: "All Locations",
    selectLocation: "Select Location",
    sno: "S.No",
    prepareReport: "Preparing your report...",
    generatingReport: "Generating absent employees report...",
    refresh: "Refresh Data",
    clearSearch: "Clear Search",
    clearFilters: "Clear Filters",
    tryAgain: "Try Again",
    errorLoading: "Error loading data",
    lastUpdated: "Last updated",
    days: "days",
    totalDays: "Total Absent Days",
    averageDays: "Avg Absent Days",
  },
  si: {
    title: "නොපැමිණීම් වාර්තාව (දින 3+)",
    fromDate: "සිට",
    toDate: "දක්වා",
    reportPeriod: "කාල සීමාව",
    datePicker: "දිනය තෝරන්න",
    exportPdf: "PDF බාගත කරන්න",
    exporting: "PDF බාගත කරමින්...",
    totalAbsent: "මුළු නිරාහාර සේවකයන් (දින 5)",
    location: "ස්ථානය",
    serviceNo: "සේවා අංකය",
    name: "සේවක නම",
    absentDays: "නිරාහාර දින",
    lastAttendance: "අවසන් පැමිණීම",
    loading: "නිරාහාර සේවක දත්ත පූරණය වෙමින්...",
    noData: "නිරාහාර සේවක දත්ත නොමැත",
    noMatchingRecords: "වත්මන් පෙරහන් සමඟ ගැලපෙන වාර්තා නොමැත",
    reportTitle: "නොපැමිණීම් වාර්තාව (දින 3+)",
    reportDate: "වාර්තා දිනය",
    page: "පිටුව",
    of: "හි",
    generatedOn: "ජනනය කරන ලද දිනය",
    pdfSuccess: "PDF සාර්ථකව බාගත කරන ලදී!",
    pdfError: "PDF බාගත කිරීමට නොහැකි විය. නැවත උත්සාහ කරන්න.",
    language: "භාෂාව",
    searchPlaceholder: "සේවා අංකය, නම හෝ ස්ථානය අනුව සොයන්න...",
    filterByLocation: "ස්ථානය අනුව පෙරහන් කරන්න",
    selectAll: "සියල්ල තෝරන්න",
    clear: "ඉවත් කරන්න",
    apply: "යොදන්න",
    clearAll: "සියල්ල ඉවත් කරන්න",
    activeFilters: "සක්‍රීය ස්ථාන පෙරහන්:",
    locationsAffected: "බලපෑමට ලක්වූ ස්ථාන",
    showing: "පෙන්වන්නේ",
    of: "න්",
    records: "වාර්තා",
    filteredBy: "පෙරහන් කර ඇත",
    location_one: "ස්ථානයක්",
    location_other: "ස්ථාන",
    allLocations: "සියලුම ස්ථාන",
    selectLocation: "ස්ථානය තෝරන්න",
    sno: "අංකය",
    prepareReport: "ඔබේ වාර්තාව සකස් කරමින්...",
    generatingReport: "නිරාහාර සේවක වාර්තාව ජනනය කරමින්...",
    refresh: "දත්ත නැවුම් කරන්න",
    clearSearch: "සෙවීම ඉවත් කරන්න",
    clearFilters: "පෙරහන් ඉවත් කරන්න",
    tryAgain: "නැවත උත්සාහ කරන්න",
    errorLoading: "දත්ත පූරණය කිරීමේ දෝෂයකි",
    lastUpdated: "අවසන් යාවත්කාලීන කිරීම",
    days: "දින",
    totalDays: "සමස්ථ නිරාහාර දින",
    averageDays: "සාමාන්‍ය නිරාහාර දින",
  },
};

// ------------ Styles (Matching RosterAbsentList) ------------
const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: "1000px",
  margin: "0 auto",
  padding: "8px 12px",
  fontFamily: "'Poppins', sans-serif",
  [theme.breakpoints.down("sm")]: {
    padding: "0px 8px",
  },
}));

// const StickyCardsContainer = styled(Box)(({ theme }) => ({
//   position: "sticky",
//   top: 0,
//   backgroundColor: "#ffffff",
//   zIndex: 100,
//   paddingTop: "0px",
//   paddingBottom: "4px",
//   boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
//   [theme.breakpoints.down("sm")]: {
//     position: "sticky",
//     top: 0,
//     backgroundColor: "#ffffff",
//   },
// }));

const ScrollableContent = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    maxHeight: "calc(100vh - 260px)",
    overflowY: "auto",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "10px",
  boxShadow: "0px 3px 8px rgba(0,0,0,0.07)",
  border: "1px solid #d1d9e6",
  backgroundColor: "#ffffff",
  overflow: "hidden",
  "& .MuiTable-root": {
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "2px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
  [theme.breakpoints.up("sm")]: {
    maxHeight: "500px",
    overflowY: "auto",
    overflowX: "auto",
  },
  [theme.breakpoints.down("sm")]: {
    maxHeight: "500px",
    overflowY: "auto",
    overflowX: "auto",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "8px 6px",
  fontSize: "13px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    letterSpacing: "0.3px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
    "&:first-of-type": {
      borderTopLeftRadius: "10px",
    },
    "&:last-of-type": {
      borderTopRightRadius: "10px",
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: "6px 4px",
    fontSize: "11px",
    "&.MuiTableCell-head": {
      fontSize: "12px",
    },
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.15s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.06)",
    cursor: "pointer",
  },
  "&:last-of-type td:first-of-type": {
    borderBottomLeftRadius: "10px",
  },
  "&:last-of-type td:last-of-type": {
    borderBottomRightRadius: "10px",
  },
}));

const FilterChip = styled(Chip)(({ theme, selected }) => ({
  margin: "4px",
  backgroundColor: selected ? "#154C79" : "#f0f4f8",
  color: selected ? "#ffffff" : "#154C79",
  fontWeight: selected ? 600 : 500,
  "&:hover": {
    backgroundColor: selected ? "#0D3A5C" : "#e0e7f0",
  },
  "& .MuiChip-deleteIcon": {
    color: selected ? "#ffffff" : "#154C79",
  },
}));

// const SummaryCard = styled(Box)(({ theme, color }) => ({
//   background: color === "error" 
//     ? "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)"
//     : "linear-gradient(135deg, #e8f0fa 0%, #d4e3f7 100%)",
//   border: color === "error" ? "1px solid #ef9a9a" : "1px solid #aec4e0",
//   padding: "15px 20px",
//   borderRadius: "12px",
//   textAlign: "center",
//   fontWeight: 600,
//   color: color === "error" ? "#b71c1c" : "#0E3C66",
//   boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
// }));

export default function AbsentLast5() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading, data, error } = useSelector((state) => state.absentLast5 || { 
    loading: false, 
    data: [], 
    error: null 
  });

  const [fromDate, setFromDate] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [toDate, setToDate] = useState(new Date());
  const [pickerTarget, setPickerTarget] = useState(null); // "from" | "to" | null
  const [language, setLanguage] = useState("en");
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Location filter states
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [tempSelectedLocations, setTempSelectedLocations] = useState([]);
  
  const openFilter = Boolean(filterAnchorEl);
  const t = translations[language];

  const toApiDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  const formatDateDisplay = (dateObj) => {
    const d = new Date(dateObj);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).split('/').join('-');
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "N/A";
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return dateTimeStr;
    }
  };

  // Load data when component mounts
  useEffect(() => {
    if (!fromDate || !toDate || isNaN(fromDate) || isNaN(toDate)) return;
    dispatch(fetchAbsentLast5(toApiDate(fromDate), toApiDate(toDate)));
  }, [dispatch, fromDate, toDate]);

  // Get unique locations from data
  const uniqueLocations = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const locations = [...new Set(data.map(item => item.locationName).filter(loc => loc))];
    return locations.sort();
  }, [data]);

  // Filter data based on search term and selected locations
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    let filtered = data;

    // Apply location filter
    if (selectedLocations.length > 0) {
      filtered = filtered.filter(item => 
        item.locationName && selectedLocations.includes(item.locationName)
      );
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          (item.sno && item.sno.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.locationName && item.locationName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  }, [data, searchTerm, selectedLocations]);

  // Statistics
  // const totalAbsent = filteredData.length;
  const totalUniqueLocations = uniqueLocations.length;
  const selectedLocationsCount = selectedLocations.length;
  
  // Calculate total absent days
  const totalAbsentDays = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (parseInt(item.AbsentDays) || 0), 0);
  }, [filteredData]);
  
  // const averageAbsentDays = filteredData.length > 0 
  //   ? Math.round(totalAbsentDays / filteredData.length) 
  //   : 0;

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    dispatch(fetchAbsentLast5(toApiDate(fromDate), toApiDate(toDate)));
    showSnackbar(t.refresh || "Data refreshed successfully", "success");
  };

  const handleDateChange = (date) => {
    if (!date || isNaN(date)) return;
    if (pickerTarget === "from") {
      setFromDate(date);
      if (date > toDate) setToDate(date);
    } else if (pickerTarget === "to") {
      setToDate(date);
      if (date < fromDate) setFromDate(date);
    }
    setPickerTarget(null); // the useEffect above reloads the data
  };

  const handleLanguageMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    handleLanguageMenuClose();
  };

  // Location filter handlers
  const handleFilterClick = (event) => {
    setTempSelectedLocations(selectedLocations);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleLocationToggle = (location) => {
    setTempSelectedLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  const handleSelectAllLocations = () => {
    if (tempSelectedLocations.length === uniqueLocations.length) {
      setTempSelectedLocations([]);
    } else {
      setTempSelectedLocations([...uniqueLocations]);
    }
  };

  const handleApplyFilter = () => {
    setSelectedLocations(tempSelectedLocations);
    handleFilterClose();
    if (tempSelectedLocations.length > 0) {
      showSnackbar(
        `${t.filteredBy} ${tempSelectedLocations.length} ${tempSelectedLocations.length === 1 ? t.location_one : t.location_other}`,
        "info"
      );
    }
  };

  const handleClearFilter = () => {
    setSelectedLocations([]);
    setTempSelectedLocations([]);
    handleFilterClose();
    showSnackbar(t.clearAll || "Location filter cleared", "info");
  };

  const handleRemoveLocation = (locationToRemove) => {
    setSelectedLocations(prev => prev.filter(loc => loc !== locationToRemove));
  };

  // ================= PDF EXPORT FUNCTION =================
  const handleExportPDF = async () => {
    if (exporting) return;
    
    try {
      setExporting(true);
      setExportProgress(10);

      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      setExportProgress(20);

      // Title
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text(t.reportTitle, pageWidth / 2, 20, { align: "center" });

      setExportProgress(30);

      // Date and Summary
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      // doc.text(`${t.reportPeriod}: ${formatDateDisplay(fromDate)} - ${formatDateDisplay(toDate)}`, 20, 35);
      // doc.text(`${t.totalAbsent}: ${totalAbsent}`, 20, 42);
      // doc.text(`${t.totalDays}: ${totalAbsentDays}`, 20, 49);
      // doc.text(`${t.averageDays}: ${averageAbsentDays}`, 20, 56);
      
      // if (selectedLocations.length > 0) {
      //   doc.text(`${t.filteredBy}: ${selectedLocations.join(', ')}`, 20, 63);
      //   doc.text(`${t.generatedOn}: ${new Date().toLocaleString()}`, 20, 70);
      // } else {
      //   doc.text(`${t.generatedOn}: ${new Date().toLocaleString()}`, 20, 63);
      // }
      doc.text(`${t.reportPeriod}: ${formatDateDisplay(fromDate)} - ${formatDateDisplay(toDate)}`, 20, 35);

      if (selectedLocations.length > 0) {
        doc.text(`${t.filteredBy}: ${selectedLocations.join(', ')}`, 20, 42);
        doc.text(`${t.generatedOn}: ${new Date().toLocaleString()}`, 20, 49);
      } else {
        doc.text(`${t.generatedOn}: ${new Date().toLocaleString()}`, 20, 42);
      }

      setExportProgress(50);

      // Table Columns - Added AbsentDays
      const columns = [t.sno, t.name, t.location, t.absentDays, t.lastAttendance];
      const rows = filteredData.map((item, index) => [
        item.sno || index + 1,
        item.name || "N/A",
        item.locationName || "N/A",
        item.AbsentDays || "0",
        formatDateTime(item.LastAttendanceDate),
      ]);

      setExportProgress(70);

      // Generate Table
      autoTable(doc, {
        head: [columns],
        body: rows,
        startY: selectedLocations.length > 0 ? 58 : 51,
        styles: {
          fontSize: 9,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [21, 76, 121],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 248, 252],
        },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 90 },
          2: { cellWidth: 70 },
          3: { cellWidth: 30, halign: 'center' },
          4: { cellWidth: 60 },
        },
        // foot: [
        //   [
        //     "TOTAL",
        //     `${filteredData.length} ${t.records}`,
        //     "",
        //     `${totalAbsentDays} ${t.days}`,
        //     "",
        //   ],
        // ],
        // footStyles: {
        //   fillColor: [21, 76, 121],
        //   textColor: 255,
        //   fontStyle: "bold",
        // },
      });

      setExportProgress(80);

      // Add footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `${t.page} ${i} ${t.of} ${pageCount}`,
          pageWidth - 20,
          pageHeight - 10,
          { align: "right" }
        );
      }

      setExportProgress(100);

      // Save the PDF
      const filename = selectedLocations.length > 0 
        ? `Absenteeism_Filtered_${toApiDate(fromDate)}_to_${toApiDate(toDate)}.pdf`
        : `Absenteeism_${toApiDate(fromDate)}_to_${toApiDate(toDate)}.pdf`;
      
      doc.save(filename);
      
      showSnackbar(t.pdfSuccess, "success");
      
      setTimeout(() => {
        setExporting(false);
        setExportProgress(0);
      }, 1000);
      
    } catch (error) {
      console.error("PDF export error:", error);
      showSnackbar(t.pdfError, "error");
      setExporting(false);
      setExportProgress(0);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        {/* Export Progress Backdrop */}
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
          open={exporting}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 4,
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              maxWidth: 400,
              width: "100%",
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{ color: "#154c79" }}
            />
            <Typography variant="h6" color="primary" fontWeight="bold">
              {t.generatingReport}
            </Typography>
            <Box sx={{ width: "100%", mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={exportProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#154c79",
                  },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mt={1}
              >
                {exportProgress}% {language === 'si' ? "සම්පූර්ණ" : "Complete"}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              {t.prepareReport}
            </Typography>
          </Box>
        </Backdrop>

        {/* Header with Language Selector */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 700,
              color: "#0e3c66",
              textAlign: "center",
              flex: 1,
            }}
          >
            {t.title}
          </Typography>
          
          {/* Language Selector */}
          <Tooltip title={t.language}>
            <IconButton
              onClick={handleLanguageMenuOpen}
              sx={{
                backgroundColor: language === 'si' ? '#154c79' : '#f0f0f0',
                color: language === 'si' ? 'white' : '#154c79',
                '&:hover': {
                  backgroundColor: language === 'si' ? '#0D47A1' : '#e0e0e0',
                },
                width: '36px',
                height: '36px',
                ml: 1,
              }}
            >
              {language === 'si' ? (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>සිං</Typography>
              ) : (
                <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>EN</Typography>
              )}
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 120,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }
            }}
          >
            <MenuItem 
              onClick={() => handleLanguageChange('en')}
              selected={language === 'en'}
              sx={{
                backgroundColor: language === 'en' ? 'rgba(21, 76, 121, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(21, 76, 121, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LanguageIcon sx={{ fontSize: 18, color: '#154c79' }} />
                <Typography sx={{ fontWeight: language === 'en' ? 600 : 400 }}>
                  English
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => handleLanguageChange('si')}
              selected={language === 'si'}
              sx={{
                backgroundColor: language === 'si' ? 'rgba(21, 76, 121, 0.08)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(21, 76, 121, 0.12)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TranslateIcon sx={{ fontSize: 18, color: '#154c79' }} />
                <Typography sx={{ fontWeight: language === 'si' ? 600 : 400 }}>
                  සිංහල
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
        </Box>

        {/* Controls Row - Matching RosterAbsentList Design */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 1.5,
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<CalendarTodayIcon />}
            onClick={() => setPickerTarget("from")}
            disabled={exporting}
            sx={{
              flex: isMobile ? 1 : 1.5,
              minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
              height: "38px",
              borderRadius: "8px",
              backgroundColor: exporting ? "#9e9e9e" : "#154c79",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "13px",
              "&:hover": { 
                backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" 
              },
            }}
          >
            {t.fromDate}: {formatDateDisplay(fromDate)}
          </Button>

          <Button
            variant="contained"
            startIcon={<CalendarTodayIcon />}
            onClick={() => setPickerTarget("to")}
            disabled={exporting}
            sx={{
              flex: isMobile ? 1 : 1.5,
              minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
              height: "38px",
              borderRadius: "8px",
              backgroundColor: exporting ? "#9e9e9e" : "#154c79",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "13px",
              "&:hover": { backgroundColor: exporting ? "#9e9e9e" : "#0D47A1" },
            }}
          >
            {t.toDate}: {formatDateDisplay(toDate)}
          </Button>

          <TextField
            placeholder={t.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{
              flex: isMobile ? 1 : 2.5,
              minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: '38px',
                backgroundColor: '#ffffff',
              },
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#999', mr: 0.5, fontSize: 18 }} />,
            }}
          />

          <Badge
            badgeContent={selectedLocationsCount}
            color="primary"
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleFilterClick}
              disabled={loading || uniqueLocations.length === 0}
              sx={{
                flex: isMobile ? 1 : 1,
                minWidth: isMobile ? "calc(33.33% - 4px)" : "auto",
                height: "38px",
                borderRadius: "8px",
                borderColor: "#154C79",
                color: "#154C79",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "13px",
                backgroundColor: selectedLocationsCount > 0 ? "#e3f2fd" : "transparent",
                "&:hover": {
                  borderColor: "#0D3A5C",
                  backgroundColor: selectedLocationsCount > 0 ? "#bbdefb" : "#f5f5f5",
                },
              }}
            >
              {t.filterByLocation}
            </Button>
          </Badge>

          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={exporting || filteredData.length === 0}
            sx={{
              flex: isMobile ? 1 : 1,
              minWidth: isMobile ? "100%" : "auto",
              height: "38px",
              borderRadius: "8px",
              backgroundColor: exporting || filteredData.length === 0 ? "#bdbdbd" : "#2e7d32",
              color: "#fff",
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "13px",
              "&:hover": { 
                backgroundColor: exporting || filteredData.length === 0 ? "#bdbdbd" : "#1b5e20" 
              },
            }}
          >
            {exporting ? t.exporting : t.exportPdf}
          </Button>

          <Tooltip title={t.refresh}>
            <IconButton
              onClick={handleRefresh}
              disabled={loading || exporting}
              sx={{
                backgroundColor: "#f0f4f8",
                "&:hover": { backgroundColor: "#e0e7f0" },
                height: "38px",
                width: "38px",
              }}
            >
              <RefreshIcon sx={{ color: "#154C79", fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Location Filter Popover */}
        <Popover
          open={openFilter}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 300,
              p: 2,
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            }
          }}
        >
          <Typography sx={{ fontWeight: 600, color: "#154C79", mb: 1, display: "flex", alignItems: "center" }}>
            <LocationOnIcon sx={{ mr: 1, fontSize: 20 }} />
            {t.filterByLocation}
          </Typography>
          
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ maxHeight: 300, overflowY: "auto", mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={tempSelectedLocations.length === uniqueLocations.length && uniqueLocations.length > 0}
                  indeterminate={tempSelectedLocations.length > 0 && tempSelectedLocations.length < uniqueLocations.length}
                  onChange={handleSelectAllLocations}
                  sx={{
                    color: "#154C79",
                    '&.Mui-checked': { color: "#154C79" },
                  }}
                />
              }
              label={t.selectAll}
              sx={{ width: '100%', mb: 1 }}
            />
            
            <Divider sx={{ mb: 1 }} />
            
            {uniqueLocations.map((location) => (
              <FormControlLabel
                key={location}
                control={
                  <Checkbox
                    checked={tempSelectedLocations.includes(location)}
                    onChange={() => handleLocationToggle(location)}
                    sx={{
                      color: "#154C79",
                      '&.Mui-checked': { color: "#154C79" },
                    }}
                  />
                }
                label={location}
                sx={{ width: '100%', display: 'block' }}
              />
            ))}
          </Box>
          
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={handleClearFilter}
              sx={{ color: "#666" }}
            >
              {t.clear}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              sx={{ backgroundColor: "#154C79" }}
            >
              {t.apply}
            </Button>
          </Box>
        </Popover>

        {pickerTarget && (
          <DatePicker
            open={Boolean(pickerTarget)}
            onClose={() => setPickerTarget(null)}
            value={pickerTarget === "from" ? fromDate : toDate}
            maxDate={new Date()}
            onChange={handleDateChange}
            renderInput={(params) => (
              <TextField {...params} sx={{ display: "none" }} />
            )}
          />
        )}

        {/* Active Filters Display */}
        {selectedLocations.length > 0 && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography sx={{ fontSize: "13px", color: "#666", mr: 1 }}>
              {t.activeFilters}
            </Typography>
            {selectedLocations.map((location) => (
              <FilterChip
                key={location}
                label={location}
                selected
                size="small"
                onDelete={() => handleRemoveLocation(location)}
                deleteIcon={<ClearIcon />}
              />
            ))}
            <Button
              size="small"
              onClick={() => setSelectedLocations([])}
              sx={{ color: "#154C79", fontSize: "12px", textTransform: "none" }}
            >
              {t.clearAll}
            </Button>
          </Box>
        )}

        {/* Sticky Stats Cards - Updated with Absent Days */}
        {/* {!loading && !error && filteredData.length > 0 && (
          <StickyCardsContainer>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: "6px",
                mb: 1,
              }}
            > */}
              {/* Total Absent Card */}
              {/* <SummaryCard color="error" sx={{ flex: 1, minWidth: 0, p: "6px 8px" }}>
                <Typography
                  sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
                >
                  {t.totalAbsent}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {totalAbsent}
                </Typography>
              </SummaryCard> */}

              {/* Total Absent Days Card */}
              {/* <SummaryCard sx={{ flex: 1, minWidth: 0, p: "6px 8px" }}>
                <Typography
                  sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
                >
                  {t.totalDays}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {totalAbsentDays}
                </Typography>
              </SummaryCard> */}

              {/* Average Days Card */}
              {/* <SummaryCard sx={{ flex: 1, minWidth: 0, p: "6px 8px" }}>
                <Typography
                  sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
                >
                  {t.averageDays}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {averageAbsentDays}
                </Typography>
              </SummaryCard> */}

              {/* Locations Affected Card */}
              {/* <SummaryCard sx={{ flex: 1, minWidth: 0, p: "6px 8px" }}>
                <Typography
                  sx={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, mb: 0.2 }}
                >
                  {t.locationsAffected}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "15px" : "18px", fontWeight: 800 }}>
                  {selectedLocations.length > 0 ? selectedLocations.length : totalUniqueLocations}
                </Typography>
                {selectedLocations.length > 0 && (
                  <Typography sx={{ fontSize: "8px", color: "#666" }}>
                    ({t.filteredBy} {totalUniqueLocations})
                  </Typography>
                )}
              </SummaryCard>
            </Box>
          </StickyCardsContainer>
        )} */}

        <ScrollableContent>
          {/* Main Table - Updated with AbsentDays column */}
          <StyledTableContainer component={Paper} sx={{ mt: 2 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell>{t.serviceNo}</StyledTableCell>
                  <StyledTableCell>{t.name}</StyledTableCell>
                  <StyledTableCell>{t.location}</StyledTableCell>
                  <StyledTableCell>{t.absentDays}</StyledTableCell>
                  <StyledTableCell>{t.lastAttendance}</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: "#154C79" }} />
                        <Typography sx={{ color: "#666", fontSize: "14px" }}>
                          {t.loading}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <PersonOffIcon sx={{ fontSize: 50, color: "#f44336" }} />
                        <Typography sx={{ color: "#f44336", fontSize: "16px", fontWeight: 500 }}>
                          {t.errorLoading}
                        </Typography>
                        <Typography sx={{ color: "#666", fontSize: "14px", maxWidth: "400px" }}>
                          {typeof error === 'string' ? error : 'Failed to fetch data from server'}
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={handleRefresh} 
                          sx={{ mt: 1, backgroundColor: "#154C79", borderRadius: "8px" }}
                        >
                          {t.tryAgain}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <PersonOffIcon sx={{ fontSize: 50, color: "#999" }} />
                        <Typography sx={{ color: "#666", fontSize: "16px" }}>
                          {searchTerm || selectedLocations.length > 0 
                            ? t.noMatchingRecords
                            : t.noData}
                        </Typography>
                        {(searchTerm || selectedLocations.length > 0) && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {searchTerm && (
                              <Button 
                                variant="outlined" 
                                onClick={() => setSearchTerm("")} 
                                sx={{ borderColor: "#154C79", color: "#154C79" }}
                              >
                                {t.clearSearch}
                              </Button>
                            )}
                            {selectedLocations.length > 0 && (
                              <Button 
                                variant="outlined" 
                                onClick={() => setSelectedLocations([])} 
                                sx={{ borderColor: "#154C79", color: "#154C79" }}
                              >
                                {t.clearFilters}
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>
                        <Chip
                          label={item.sno || index + 1}
                          size="small"
                          sx={{
                            backgroundColor: "#154C79",
                            color: "white",
                            fontWeight: 600,
                            minWidth: "45px",
                            borderRadius: "8px",
                            fontSize: "11px",
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left", fontWeight: 500 }}>
                        {item.name || "N/A"}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={item.locationName || "N/A"}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: "#154C79",
                            color: "#154C79",
                            fontWeight: 500,
                            borderRadius: "8px",
                            fontSize: "11px",
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Chip
                          label={`${item.AbsentDays || 0} ${t.days}`}
                          size="small"
                          sx={{
                            backgroundColor: parseInt(item.AbsentDays) > 20 ? "#d32f2f" : 
                                           parseInt(item.AbsentDays) > 10 ? "#ed6c02" : "#2e7d32",
                            color: "white",
                            fontWeight: 600,
                            borderRadius: "8px",
                            fontSize: "11px",
                          }}
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={{ fontSize: "11px", color: "#666" }}>
                        {formatDateTime(item.LastAttendanceDate)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                )}
                
                {/* Total Row - Show when data exists */}
                {!loading && !error && filteredData.length > 0 && (
                  <StyledTableRow
                    sx={{
                      backgroundColor: "#154c79 !important",
                      "&:hover": { backgroundColor: "#154c79 !important" },
                      position: "sticky",
                      bottom: 0,
                      zIndex: 5,
                    }}
                  >
                    <StyledTableCell
                      sx={{
                        color: "#ffffff",
                        fontWeight: 700,
                      }}
                    >
                      {t.total}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                      colSpan={1}
                    >
                      {filteredData.length} {t.records}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      {selectedLocations.length > 0 ? selectedLocations.length : totalUniqueLocations} {t.location.toLowerCase()}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      {totalAbsentDays} {t.days}
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ color: "#ffffff", fontWeight: 700 }}
                    >
                      -
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Footer with record count */}
          {!loading && !error && filteredData.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, mb: 1 }}>
              <Typography sx={{ fontSize: "12px", color: "#666" }}>
                {t.showing} {filteredData.length} {t.of} {data?.length || 0} {t.records}
                {selectedLocations.length > 0 && ` (${t.filteredBy} ${selectedLocations.length} ${selectedLocations.length === 1 ? t.location_one : t.location_other})`}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}>
                {t.lastUpdated}: {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          )}
        </ScrollableContent>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </PageContainer>
    </LocalizationProvider>
  );
}